import { SheetModeControls } from "@/components/r3f/controls/sheet-mode-controls";
import { PointCloudSubscene } from "@/components/r3f/effects/point-cloud-subscene";
import { CadRenderer } from "@/components/r3f/renderers/cad-renderer";
import { PointCloudRenderer } from "@/components/r3f/renderers/pointcloud-renderer";
import { SheetRenderer } from "@/components/r3f/renderers/sheet-renderer";
import { useSceneBackgroundColor } from "@/components/r3f/utils/scene-background";
import {
  centerOrthoCamera,
  useCenterCameraOnPlaceholders,
  useOrthoCameraRotationFromSheet,
} from "@/hooks/use-center-camera-on-placeholders";
import { useLoadIElements } from "@/hooks/use-load-ielements";
import {
  useCadModelObjectLimitedBoundingBox,
  useObjectBoundingBox,
} from "@/hooks/use-object-bounding-box";
import { useObjectVisibility } from "@/hooks/use-object-visibility";
import {
  CadModelObject,
  useCached3DObject,
  useCached3DObjectIfExists,
} from "@/object-cache";
import {
  selectWizardElementToAlignId,
  selectWizardPointCloudStreamToAlignId,
  selectWizardReferenceElementId,
  selectWizardReferencePointCloudStreamId,
} from "@/store/modes/alignment-wizard-mode-selectors";
import {
  resetWizardReferenceElementId,
  setWizardPointCloudStreamToAlignId,
  setWizardReferencePointCloudStreamId,
} from "@/store/modes/alignment-wizard-mode-slice";
import { RootState } from "@/store/store";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { setShowSpinner } from "@/store/ui/ui-slice";
import { isObjPointCloudPoint } from "@/types/threejs-type-guards";
import {
  CopyToScreenPass,
  EffectPipelineWithSubScenes,
  ExplorationControls,
  FilteredRenderPass,
  Map2DControlsRef,
  UniformLights,
  useNonExhaustiveEffect,
  useToast,
  View,
} from "@faro-lotv/app-component-toolbox";
import { assert } from "@faro-lotv/foundation";
import {
  IElement,
  IElementAreaSection,
  IElementGenericDataSession,
  IElementGenericImgSheet,
  IElementImg360,
  IElementModel3dStream,
  IElementPointCloudStream,
  IElementType,
  isIElementAreaSection,
  isIElementGenericDataSession,
  isIElementGenericImgSheet,
  isIElementModel3dStream,
  isIElementPointCloudStream,
} from "@faro-lotv/ielement-types";
import { reproportionCamera } from "@faro-lotv/lotv";
import {
  selectChildDepthFirst,
  selectIElement,
} from "@faro-lotv/project-source";
import { useThree } from "@react-three/fiber";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { OrthographicCamera, PerspectiveCamera, Vector2, Vector3 } from "three";
import {
  useNewOrthographicCamera,
  useNewPerspectiveCamera,
} from "../alignment-modes-commons/align-to-cad-utils";
import {
  FLOOR_OVERLAP,
  NEAR_PLANE_DISTANCE,
} from "../alignment-modes-commons/model-elevation-scene";
import { useOverlayElements } from "../overlay-elements-context";
import { selectIsPointCloudViewable } from "../select-current-area-data";

/** @returns Rendered element for Alignment Wizard */
export function AlignWizardModeScene(): JSX.Element | null {
  const { firstScreen, secondScreen } = useOverlayElements();

  const dispatch = useAppDispatch();
  const elementToAlignId = useAppSelector(selectWizardElementToAlignId);
  const pointCloudStreamToAlignId = useAppSelector(
    selectWizardPointCloudStreamToAlignId,
  );
  const updatePointCloudStreamToAlignId = useCallback(
    (id?: string) => dispatch(setWizardPointCloudStreamToAlignId(id)),
    [dispatch],
  );

  const referenceElementId = useAppSelector(selectWizardReferenceElementId);
  const referencePointCloudStreamId = useAppSelector(
    selectWizardReferencePointCloudStreamId,
  );
  const updateReferencePointCloudStreamId = useCallback(
    (id?: string) => dispatch(setWizardReferencePointCloudStreamId(id)),
    [dispatch],
  );

  return (
    <>
      {firstScreen && elementToAlignId && (
        <AlignmentWizardView
          elementId={elementToAlignId}
          pointCloudStreamId={pointCloudStreamToAlignId}
          updatePointCloudStreamId={updatePointCloudStreamToAlignId}
          screen={firstScreen}
        />
      )}

      {secondScreen && referenceElementId && (
        <AlignmentWizardView
          elementId={referenceElementId}
          pointCloudStreamId={referencePointCloudStreamId}
          updatePointCloudStreamId={updateReferencePointCloudStreamId}
          screen={secondScreen}
        />
      )}
    </>
  );
}

type AlignmentWizardViewProps = {
  /** element to display in view*/
  elementId: string;

  /** id of the point cloud stream used for alignment when the element contains multiple streams */
  pointCloudStreamId?: string;

  /** Callback function to update the point cloud stream id if not provided after loading session */
  updatePointCloudStreamId(id?: string): void;

  /** screen to render view */
  screen: HTMLDivElement;
};

/** @returns Rendered one of views of Alignment Wizard */
function AlignmentWizardView({
  elementId,
  pointCloudStreamId,
  updatePointCloudStreamId,
  screen,
}: AlignmentWizardViewProps): JSX.Element | null {
  const background = useSceneBackgroundColor();

  // both left and right cameras could be perspective for cloud/cad or orthographic for sheet
  const orthographicCamera = useNewOrthographicCamera();
  const perspectiveCamera = useNewPerspectiveCamera();

  const element = useAppSelector(selectIElement(elementId));
  assert(element, "invalid element cannot be rendered");

  const isOrthographicCamera =
    isIElementAreaSection(element) || isIElementGenericImgSheet(element);

  const aspectRatio = screen.clientWidth / screen.clientHeight;

  return (
    <View
      camera={isOrthographicCamera ? orthographicCamera : perspectiveCamera}
      trackingElement={screen}
      background={background}
      hasSeparateScene
    >
      {isOrthographicCamera && (
        <SheetSceneView
          camera={orthographicCamera}
          viewAspectRatio={aspectRatio}
          areaOrSheet={element}
        />
      )}
      {isIElementGenericDataSession(element) && (
        <CloudSceneView
          camera={perspectiveCamera}
          cloudDataSession={element}
          pointCloudStreamId={pointCloudStreamId}
          updatePointCloudStreamId={updatePointCloudStreamId}
        />
      )}

      {isIElementModel3dStream(element) && (
        <CadSceneView camera={perspectiveCamera} cad={element} />
      )}
    </View>
  );
}

type SheetSceneViewProps = {
  /** The area or sheet to align */
  areaOrSheet: IElementAreaSection | IElementGenericImgSheet;

  /** camera for view */
  camera: OrthographicCamera;

  /** view aspect ration*/
  viewAspectRatio: number;
};

/** @returns Rendered sheet in one of views of Alignment Wizard */
function SheetSceneView({
  camera,
  viewAspectRatio,
  areaOrSheet,
}: SheetSceneViewProps): JSX.Element | null {
  // https://faro01.atlassian.net/browse/CADBIM-1205 will later take all visible sheets into account.
  const sheetElement = useAppSelector(
    selectChildDepthFirst(areaOrSheet, isIElementGenericImgSheet),
  );
  assert(sheetElement, "Area does not have a sheet");

  const cachedSheet = useCached3DObject(
    sheetElement,
    "AlignWizardSheetSceneView",
  );

  const sheetElements = useMemo(() => [sheetElement], [sheetElement]);
  const cameraRotation = useOrthoCameraRotationFromSheet(sheetElement.id);
  const sheetCenteringData = useCenterCameraOnPlaceholders({
    sheetElements,
    cameraRotation,
    placeholders: new Array<IElementImg360>(),
    viewAspectRatio,
  });

  const controlsRef = useRef<Map2DControlsRef | undefined>(undefined);

  // When the sheet element changes, re-center the camera.
  useNonExhaustiveEffect(() => {
    centerOrthoCamera(camera, sheetCenteringData);
    if (controlsRef.current) {
      // Even the control holds reference to the camera, it will NOT sync with
      // the new camera data if it is in inertia movement. Update the camera
      // explicitly to make sure its internal states are synced correctly.
      controlsRef.current.camera = camera;
    }
  }, [sheetElement]);

  return (
    <>
      <SheetRenderer sheet={cachedSheet} />
      <SheetModeControls camera={camera} ref={controlsRef} />
    </>
  );
}

type CloudSceneViewProps = {
  /**
   * The selected DataSession of te reference cloud.
   *  The alignment wizard is listing DataSessions in the "clouds" folder;
   */
  cloudDataSession: IElementGenericDataSession;

  /** Id of the point cloud stream from the data session */
  pointCloudStreamId?: string;

  /** Callback function to update the point cloud stream id if not provided after loading session */
  updatePointCloudStreamId(id?: string): void;

  /** camera for view */
  camera: PerspectiveCamera;
};

/**
 * Try to get a cloud that can be used as a reference for alignment from a given DataSession.
 * If the DataSession is not loaded, we will NOT load the DataSession and will return undefined.
 *
 * @param dataSession DataSession being tested
 * @param state current store state
 * @returns a IElementPointCloudStream if the DataSession is loaded and contain a usable cloud; undefined otherwise
 */
export function getValidReferenceCloud(
  dataSession: IElementGenericDataSession,
  state: RootState,
): IElement | undefined {
  return selectChildDepthFirst(
    dataSession,
    (el) =>
      isIElementPointCloudStream(el) && selectIsPointCloudViewable(el)(state),
  )(state);
}

/** @returns Rendered cloud in one of views OR null during loading process */
function CloudSceneView({
  camera,
  pointCloudStreamId,
  updatePointCloudStreamId,
  cloudDataSession,
}: CloudSceneViewProps): JSX.Element | null {
  const dispatch = useAppDispatch();
  const { openToast } = useToast();
  const [isLoaded, setIsLoaded] = useState(false);

  const cloudElement = useAppSelector(selectIElement(pointCloudStreamId));

  const updateLoadingStatus = useCallback(
    (loading: boolean) => setIsLoaded(!loading),
    [],
  );

  useEffect(() => {
    dispatch(setShowSpinner(!cloudElement && !isLoaded));

    if (pointCloudStreamId && !cloudElement && isLoaded) {
      dispatch(resetWizardReferenceElementId());
      openToast({
        title: "Point cloud not available",
        variant: "info",
        message:
          "The selected reference element does not contain a point cloud and cannot be used for alignment. Please choose a different reference element",
      });
    }
  }, [cloudElement, dispatch, isLoaded, openToast, pointCloudStreamId]);

  return pointCloudStreamId &&
    cloudElement &&
    isIElementPointCloudStream(cloudElement) ? (
    <CloudView camera={camera} cloudElement={cloudElement} />
  ) : (
    <CloudLoading
      cloudDataSession={cloudDataSession}
      pointCloudStreamId={pointCloudStreamId}
      updatePointCloudStreamId={updatePointCloudStreamId}
      updateLoadingStatus={updateLoadingStatus}
    />
  );
}

type CloudViewProps = {
  /** The point cloud stream iElement to align */
  cloudElement: IElementPointCloudStream;

  /** camera for view */
  camera: PerspectiveCamera;
};

/** @returns Rendered cloud in one of views of Alignment Wizard */
function CloudView({
  camera,
  cloudElement,
}: CloudViewProps): JSX.Element | null {
  const pointCloud = useCached3DObject(cloudElement, "AlignWizardCloudView");

  const cloudBox = useObjectBoundingBox(pointCloud, pointCloud.iElement.id);
  assert(cloudBox, "Cloud bounding box must exist");
  const { cloudCenter } = useMemo(() => {
    const bboxSize = cloudBox.getSize(new Vector3());
    const extents = new Vector2(
      bboxSize.x * FLOOR_OVERLAP,
      bboxSize.z * FLOOR_OVERLAP,
    );
    const cloudCenter = cloudBox.getCenter(new Vector3());
    return { extents, cloudCenter };
  }, [cloudBox]);

  const size = useThree((s) => s.size);

  useEffect(() => {
    const diagonal = cloudBox.getSize(new Vector3()).length();
    camera.position.set(
      cloudCenter.x - diagonal,
      cloudCenter.y + 0.1 * diagonal,
      cloudCenter.z - 0.5 * diagonal,
    );

    camera.updateMatrixWorld(true);
    const cameraFarFactor = 50;
    camera.far = diagonal * cameraFarFactor;
    camera.near = NEAR_PLANE_DISTANCE;
    reproportionCamera(camera, size.width / size.height);
  }, [cloudBox, camera, cloudCenter, size]);

  useObjectVisibility(pointCloud, true);

  return (
    <>
      <UniformLights />
      <PointCloudRenderer pointCloud={pointCloud} />
      <EffectPipelineWithSubScenes>
        <PointCloudSubscene pointCloud={pointCloud} />
        <FilteredRenderPass
          filter={(o) => !isObjPointCloudPoint(o)}
          clear={false}
          clearDepth={false}
        />
        <CopyToScreenPass />
      </EffectPipelineWithSubScenes>

      <ExplorationControls
        enabled
        target={cloudCenter}
        disableInertia
        hidePivot
      />
    </>
  );
}

type CloudLoadingProps = {
  /** The cloud data session section to load required elements for point cloud */
  cloudDataSession: IElementGenericDataSession;
  /** Id of the point cloud stream from the data session */
  pointCloudStreamId?: string;
  /** Callback function to update the point cloud stream id if not provided after loading session */
  updatePointCloudStreamId(id?: string): void;
  /** Callback function to update the loading status */
  updateLoadingStatus(loading: boolean): void;
};

/**
 * Load elements required for cloud scene view
 *
 * @returns null
 */
function CloudLoading({
  cloudDataSession,
  pointCloudStreamId,
  updatePointCloudStreamId,
  updateLoadingStatus,
}: CloudLoadingProps): null {
  const store = useAppStore();

  const isLoading = useLoadIElements([
    // Load ancestors (needed for world transforms and sheet)
    {
      descendantIds: [cloudDataSession.id],
    },
    // Load descendants (needed for point cloud streams)
    {
      ancestorIds: [cloudDataSession.id],
      types: [IElementType.section, IElementType.pointCloudStream],
    },
  ]);

  useEffect(() => {
    if (!isLoading) {
      // The point cloud stream could be undefined when the data session is not loaded.
      // Pick the first valid point cloud stream as the default.
      if (!pointCloudStreamId) {
        const cloudStream = getValidReferenceCloud(
          cloudDataSession,
          store.getState(),
        );
        updatePointCloudStreamId(cloudStream?.id);
      }
    }
    updateLoadingStatus(isLoading);
  }, [
    cloudDataSession,
    isLoading,
    pointCloudStreamId,
    store,
    updateLoadingStatus,
    updatePointCloudStreamId,
  ]);

  return null;
}

type CadSceneViewProps = {
  /** The cad to display in view */
  cad: IElementModel3dStream;

  /** camera for view */
  camera: PerspectiveCamera;
};

/**
 * @returns Rendered cad in one of views of Alignment Wizard
 * this component trying to load cad model. In case if cad model is available it will be rendered.
 * If cad model is not available single time popup toast with error message rendered
 */
function CadSceneView({ camera, cad }: CadSceneViewProps): JSX.Element | null {
  const cadModel = useCached3DObjectIfExists(cad, "AlignWizardCadSceneView");
  const dispatch = useAppDispatch();

  if (!cadModel) {
    dispatch(resetWizardReferenceElementId());
    return null;
  }

  return <CadSceneViewUsingCacheObject cadModel={cadModel} camera={camera} />;
}

type CadSceneViewUsingCacheObjectProps = {
  /** The cad to display in view */
  cadModel: CadModelObject;

  /** camera for view */
  camera: PerspectiveCamera;
};

/**
 * @returns Rendered cad in one of views of Alignment Wizard using cadModel cache object
 */
function CadSceneViewUsingCacheObject({
  camera,
  cadModel,
}: CadSceneViewUsingCacheObjectProps): JSX.Element | null {
  useEffect(() => {
    // reset any highlight state from the cadModel
    for (const objectId of cadModel.objectIds) {
      cadModel.setObjectHighlighted(objectId, false);
    }
  }, [cadModel]);

  useObjectVisibility(cadModel, true);
  const size = useThree((s) => s.size);

  const cadBox = useCadModelObjectLimitedBoundingBox(
    cadModel,
    cadModel.iElement.id,
  );

  const cadCenter = useMemo(
    () =>
      cadBox.boundingBox
        ? cadBox.boundingBox.getCenter(new Vector3())
        : new Vector3(),
    [cadBox],
  );

  useEffect(() => {
    const diagonal = cadBox.boundingBox
      ? cadBox.boundingBox.getSize(new Vector3()).length()
      : 0;
    camera.position.set(
      cadCenter.x - diagonal,
      cadCenter.y + 0.1 * diagonal,
      cadCenter.z - 0.5 * diagonal,
    );

    camera.updateMatrixWorld(true);
    const cameraFarFactor = 50;
    camera.far = diagonal * cameraFarFactor;
    camera.near = NEAR_PLANE_DISTANCE;
    reproportionCamera(camera, size.width / size.height);
  }, [cadBox, cadCenter, camera, size]);

  return (
    <>
      <UniformLights />
      <CadRenderer cadModel={cadModel} />
      <ExplorationControls
        enabled
        target={cadCenter}
        disableInertia
        hidePivot
      />
    </>
  );
}
